import React, { useMemo } from 'react';

import { AnalogLink } from '@paragon/contract-library';
import { RaycastOrder, RenderOrder } from '@paragon/three-shared';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { getTransformationPropsForSegment } from '../../library/utilities/updateMatrixFromSegment';
import { useClickItem } from '../../store/actions/sceneInteractionActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import { getObjectCapacityColor } from '../../store/selectors/getObjectCapacityColor';
import { getSingleSelectedItemEntryOrNull } from '../../store/selectors/getSelectedItemSelector';

const defaultColor = 0x6e88e3;
const exemptFromChecksColor = 0x007a00;
const selectedColor = 0xe74c3c;

export function AnalogLinkVisual({ analogLink }: { analogLink: AnalogLink }) {
  const dispatch = useAppDispatch();

  const hidden = useAppSelector((state) => state.visualOptions.hiddenObjectGuids).includes(analogLink.guid);

  const selectedEntry = useAppSelector(getSingleSelectedItemEntryOrNull);
  const selected = selectedEntry?.type === 'analogLink' && selectedEntry.item.guid === analogLink.guid;

  const colorFunc = useAppSelector(getObjectCapacityColor);
  const color = useMemo(
    () => colorFunc({ itemGuid: analogLink.guid, itemType: 'analogLink' }),
    [colorFunc, analogLink.guid],
  );
  const isColorSet = color !== undefined;

  const onClick = useClickItem({ type: 'analogLink', item: analogLink });

  const item = { type: 'analogLink', guid: analogLink.guid, origin: 'fiber' } as const;

  return !hidden ? (
    <mesh
      renderOrder={RenderOrder.AnalogLinkVisual}
      {...getTransformationPropsForSegment(analogLink.iJoint, analogLink.jJoint)}
    >
      <cylinderGeometry args={[0.25, 0.25, 1, 8, 1, false]} />
      <meshBasicMaterial
        color={selected ? selectedColor : color ? color : isColorSet ? exemptFromChecksColor : defaultColor}
      />
      <mesh
        onClick={onClick}
        onPointerOver={(event) => {
          event.stopPropagation();
          dispatch(ensureHoverItemIsActive(item));
        }}
        onPointerOut={() => dispatch(clearHoverItemIfActive(item))}
        userData-raycastOrder={RaycastOrder.AnalogLinkVisual}
      >
        <cylinderGeometry args={[0.5, 0.5, 1, 8, 1, false]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>
    </mesh>
  ) : null;
}
